import { Injectable } from '@angular/core';
import { FormGroup, FormArray, AbstractControl } from '@angular/forms';
import moment from 'moment';

/**
 * Field configuration from template API
 */
export interface FieldConfig {
  id: string;
  field_name: string;
  display_name: string;
  field_type: string;
  data_type: string;
  is_mandatory: boolean;
  is_visible: boolean;
  is_custom_field: boolean;
  tab_id: string | number;
  sub_tab_id: number;
  display_order: number;
  validation_rules?: string;
  sub_tab_key?: string;
  sub_type?: string;
  type?: string;
  content?: FieldConfig[]; // For table sub-tabs
  [key: string]: any; // Allow additional fields from template API
}

/**
 * Configuration for payload generation
 */
export interface PayloadGeneratorConfig<T> {
  formGroup: FormGroup;
  payloadInterface?: new () => T; // Optional: class that matches interface structure for property ordering
  propertyOrder?: string[]; // Optional: explicit property order (alternative to payloadInterface)
  fields: FieldConfig[]; // Fields array from template API
  entityId?: string | number; // For update requests, the main entity ID
  additionalData?: Record<string, any>;
  tableFormArrays?: Map<string, FormArray>; // For handling table/array data
  getFieldKey?: (fieldName: string) => string; // Optional function to transform field names
  includeStructFields?: boolean; // Default: true. Include fields from interface/struct that are in FormGroup but not in template fields.
  skipFields?: string[]; // Fields to skip (e.g., ['vendor_code', 'transaction_no'])
  fieldToInterfaceMap?: Record<string, string>; // Map field_name directly to interface location (e.g., { 'registration_type': 'basic_details' })
  tableToInterfaceMap?: Record<string, string>; // Map table/subTab key/name to interface location (e.g., { 'vendor_contact': 'contacts' })
  dynamicFormTabs?: any[]; // Dynamic form tabs configuration
  extractObjectId?: (value: any, fieldName: string) => any; // Custom function to extract ID from autocomplete objects
  mergeTabs?: Record<string, string>; // Map of source tab to target tab for merging (e.g., { 'bank_information': 'account_information' })
  transformPayload?: (payload: any) => any; // Optional final payload transformation
  arrayKeys?: string[]; // Explicit list of keys that should be arrays (e.g., ['notes', 'phone_calls', 'files'])
}

/**
 * Validation error for missing required fields
 */
export class PayloadValidationError extends Error {
  constructor(public missingFields: string[]) {
    // missingFields contains formatted error messages (e.g., "Missing required fields: Vendor", "Missing required fields in Items Table row 1: Item, Quantity")
    // Join them with periods and spaces for readability
    const errorMessage = missingFields.length > 0
      ? missingFields.join('. ')
      : 'Missing required fields';
    super(errorMessage);
    this.name = 'PayloadValidationError';
    // Store the message property explicitly for compatibility
    this.message = errorMessage;
  }

  /**
   * Get formatted error message with proper grouping
   * This ensures the messages are displayed correctly when error.message is accessed
   */
  getFormattedMessage(): string {
    return this.missingFields.join('. ');
  }
}

/**
 * Centralized service for generating payloads from FormGroup and fields array
 * Maintains field order as defined in TypeScript interfaces
 * Handles all transformations currently done in onSubmit methods
 */
@Injectable({
  providedIn: 'root'
})
export class PayloadGeneratorService {

  /**
   * Generate payload from FormGroup using fields array and interface structure
   */
  generatePayload<T>(config: PayloadGeneratorConfig<T>): T {
    const {
      formGroup,
      payloadInterface,
      propertyOrder,
      fields,
      entityId,
      additionalData = {},
      tableFormArrays = new Map(),
      getFieldKey = (name: string) => this.defaultGetFieldKey(name),
      includeStructFields = true,
      skipFields = [],
      fieldToInterfaceMap = {},
      tableToInterfaceMap = {},
      dynamicFormTabs = [],
      extractObjectId = (value: any, fieldName?: string) => this.defaultExtractObjectId(value, fieldName || ''),
      mergeTabs = {},
      transformPayload,
      arrayKeys = []
    } = config;

    // Get property order from interface instance or provided propertyOrder array
    let interfaceKeys: string[] = [];
    if (propertyOrder && propertyOrder.length > 0) {
      interfaceKeys = propertyOrder;
    } else if (payloadInterface) {
      const interfaceInstance = new payloadInterface();
      interfaceKeys = Object.keys(interfaceInstance as Record<string, any>);
    }

    // Extract form values using getRawValue() to include disabled fields
    const rawFormValues = formGroup.getRawValue?.() ?? formGroup.value;
    // Step 1: Transform form values by data type
    const transformedValues = this.transformFormValuesByDataType(
      rawFormValues,
      fields,
      getFieldKey,
      dynamicFormTabs,
      tableFormArrays,
      extractObjectId
    );


    // Step 2: Build all field values using fieldToInterfaceMap exclusively
    // All fields are processed uniformly - destination determined solely by fieldToInterfaceMap
    // Primary entity key is the first key in interfaceKeys (typically the main entity like 'vendor', 'vendor_po', etc.)
    // Fallback: if interfaceKeys is empty, try to determine from fieldToInterfaceMap or use first fieldData key
    let primaryEntityKey: string | null = null;
    if (interfaceKeys.length > 0) {
      primaryEntityKey = interfaceKeys[0];
    } else if (Object.keys(fieldToInterfaceMap).length > 0) {
      // If no interfaceKeys, try to infer from fieldToInterfaceMap
      // The primary entity is usually the first value that appears (e.g., 'vendor', 'vendor_po')
      const firstValue = Object.values(fieldToInterfaceMap)[0];
      primaryEntityKey = firstValue || null;
    }
    const fieldData = this.buildAllFieldValues(
      transformedValues,
      fields,
      getFieldKey,
      skipFields,
      fieldToInterfaceMap,
      extractObjectId,
      entityId,
      dynamicFormTabs,
      primaryEntityKey
    );

    const tableData = this.buildTableFieldValues(
      transformedValues,
      dynamicFormTabs,
      tableFormArrays,
      getFieldKey,
      tableToInterfaceMap,
      extractObjectId
    );

    // Step 5: Collect custom fields
    const customFields = this.collectCustomFields(
      transformedValues,
      fields,
      getFieldKey
    );

    // Step 6: Merge tabs if configured
    this.mergeTabs(fieldData, mergeTabs);

    // Step 7: Build payload structure maintaining interface order
    const payload: any = {};

    // Add all field data (organized by fieldToInterfaceMap destinations)
    // Handle empty string destination (root-level fields) separately from nested objects
    Object.keys(fieldData).forEach(destination => {
      if (destination === '') {
        // Empty string means root-level - flatten these fields to payload root
        Object.assign(payload, fieldData[destination]);
      } else {
        // Non-empty destination means nested object (e.g., 'grn_details', 'vendor_po', etc.)
        payload[destination] = fieldData[destination];
      }
    });

    // Add table data
    Object.assign(payload, tableData);

    // Note: We no longer initialize empty objects/arrays for expected keys
    // Empty objects will be cleaned up later, and empty arrays are only added if explicitly needed
    // This prevents sending empty objects {} when fields don't have values

    // Add custom fields
    if (Object.keys(customFields).length > 0) {
      payload['custom_fields'] = customFields;
    }

    // Step 8: Include struct fields not in template array (if enabled)
    if (includeStructFields) {
      this.includeStructFields(payload, interfaceKeys, transformedValues, getFieldKey, extractObjectId);
    }

    // Step 9: Merge additional data
    if (Object.keys(additionalData).length > 0) {
      Object.assign(payload, additionalData);
    }

    // Step 10: Apply final transformation if provided
    const finalPayload = transformPayload ? transformPayload(payload) : payload;

    // Step 10.5: Clean up empty objects and convert to null
    this.cleanupEmptyObjects(finalPayload);

    // Step 11: Validate required fields
    this.validateRequiredFields(fields, finalPayload, getFieldKey, skipFields, fieldToInterfaceMap, formGroup, dynamicFormTabs);

    return finalPayload as T;
  }

  /**
   * Transform form values by data type (matching transformFormValuesByDataType from components)
   */
  private transformFormValuesByDataType(
    formValues: any,
    fields: FieldConfig[],
    getFieldKey: (name: string) => string,
    dynamicFormTabs: any[],
    tableFormArrays: Map<string, FormArray>,
    extractObjectId: (value: any, fieldName: string) => any
  ): any {
    const transformedValues = { ...formValues };
    const processedKeys = new Set<string>();

    // Transform regular fields
    fields.forEach(field => {
      const key = getFieldKey(field.field_name);
      if (processedKeys.has(key)) {
        return;
      }
      processedKeys.add(key);

      const value = transformedValues[key];
      const shouldConvert = value !== undefined && (value !== null || this.isBooleanRadioField(field));
      if (!shouldConvert) {
        return;
      }


      const converted = this.convertToDataType(value, field.data_type, field);
      transformedValues[key] = converted;
    });

    // Transform table data
    this.transformTableData(transformedValues, dynamicFormTabs, tableFormArrays, getFieldKey, extractObjectId);

    return transformedValues;
  }

  /**
   * Transform table data
   */
  private transformTableData(
    formValues: any,
    dynamicFormTabs: any[],
    tableFormArrays: Map<string, FormArray>,
    getFieldKey: (name: string) => string,
    extractObjectId: (value: any, fieldName: string) => any
  ): void {
    dynamicFormTabs.forEach(tab => {
      tab.sub_tabs?.forEach((subTab: any) => {
        if (!this.isTableSubTab(subTab)) {
          return;
        }

        const tableKey = getFieldKey(subTab.key || subTab.name || '');
        const tableArray = tableFormArrays.get(tableKey);

        if (tableArray && tableArray.length > 0) {
          const transformedRows = tableArray.controls
            .map((control: AbstractControl) => {
              if (!(control instanceof FormGroup)) {
                return null;
              }
              const rowValue = control.getRawValue();
              return this.transformTableRow(rowValue, subTab, getFieldKey, extractObjectId);
            })
            .filter((row: any) => row !== null && Object.keys(row).length > 0);

          formValues[tableKey] = transformedRows;
        } else {
          // Fallback to formValues if FormArray is not available
          const rawTable = formValues[tableKey];
          if (Array.isArray(rawTable)) {
            const transformedRows = rawTable
              .map((row: any) => this.transformTableRow(row, subTab, getFieldKey, extractObjectId))
              .filter((row: any) => row !== null && Object.keys(row).length > 0);
            formValues[tableKey] = transformedRows;
          }
        }
      });
    });
  }

  /**
   * Transform a single table row
   */
  private transformTableRow(
    sourceRow: any,
    subTab: any,
    getFieldKey: (name: string) => string,
    extractObjectId: (value: any, fieldName: string) => any
  ): any {
    const transformedRow: any = {};
    const customFieldsData: Record<string, any> = {};
    const fieldConfigs = subTab.content || [];
    const fieldConfigMap = new Map<string, FieldConfig>();

    // Build field config map for quick lookup
    fieldConfigs.forEach((field: FieldConfig) => {
      const fieldKey = getFieldKey(field.field_name);
      fieldConfigMap.set(fieldKey, field);
    });

    // Track extracted names from ID objects (e.g., item_master_name from item_id)
    const extractedNames: Record<string, string> = {};

    // Log source row for debugging (especially for items table)
    const subTabKey = subTab.key || subTab.name || '';

    // Process all fields from source row
    Object.keys(sourceRow || {}).forEach(key => {
      // if (key === '_selected') {
      //   return; // Skip internal selection field
      // }

      const value = sourceRow[key];
      const fieldConfig = fieldConfigMap.get(key);

      // Extract ID from object values using extractObjectId callback
      // Also extract associated name fields (e.g., item_master_name -> item_name)
      let processedValue = value;
      if (value && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
        const fieldName = fieldConfig?.field_name || key;
        // Use extractObjectId callback for all object values
        processedValue = extractObjectId(value, fieldName);


        // Extract associated name fields from the object (for backward compatibility)
        // For item_id, extract item_master_name or name/label as item_name
        if (fieldName === 'item_id') {
          const itemName = value.item_master_name ?? value.name ?? value.label ?? value.item_name ?? '';
          if (itemName) {
            extractedNames['item_name'] = itemName;
          }
        }
        // For item_name field that contains an object with id (common in credit note template),
        // extract item_id from it and set item_name to the display name
        else if (fieldName === 'item_name') {
          // Extract item_id from the object
          const extractedItemId = value.id ?? value.value ?? value.item_id ?? value.item_master_id ?? null;
          if (extractedItemId) {
            extractedNames['item_id'] = extractedItemId;
          }
          // processedValue will be the extracted ID, but we also want to keep the name
          const itemDisplayName = value.item_master_name ?? value.name ?? value.label ?? value.item_name ?? '';
          if (itemDisplayName) {
            // Override processedValue with the display name
            processedValue = itemDisplayName;
          }
        }
        // For service_id, extract service_name
        else if (fieldName === 'service_id') {
          const serviceName = value.service_name ?? value.name ?? value.label ?? value.item_master_name ?? '';
          if (serviceName) {
            extractedNames['service_name'] = serviceName;
          }
        }
        // For account_id or account, extract account name
        else if (fieldName === 'account_id' || fieldName === 'account') {
          const accountName = value.account_name ?? value.name ?? value.label ?? '';
          if (accountName) {
            extractedNames['account_name'] = accountName;
          }
        }
      }

      // Handle custom fields - collect them separately
      if (fieldConfig?.is_custom_field) {
        const shouldConvert = processedValue !== undefined && (processedValue !== null || this.isBooleanRadioField(fieldConfig));
        if (shouldConvert) {
          const converted = this.convertToDataType(processedValue, fieldConfig.data_type, fieldConfig);
          if (converted !== null && converted !== undefined && converted !== '') {
            customFieldsData[fieldConfig.field_name] = converted;
          }
        }
        return;
      }

      // Handle regular fields
      const shouldConvert = processedValue !== undefined && (processedValue !== null || (fieldConfig && this.isBooleanRadioField(fieldConfig)));
      if (!shouldConvert) {
        return;
      }

      const converted = fieldConfig
        ? this.convertToDataType(processedValue, fieldConfig.data_type, fieldConfig)
        : processedValue;

      // For boolean fields, always include the value (even if false)
      if (fieldConfig && (fieldConfig.data_type?.toLowerCase() === 'boolean' || this.isBooleanRadioField(fieldConfig))) {
        transformedRow[fieldConfig.field_name] = converted === null ? false : converted;
      } else if (fieldConfig && converted !== null && converted !== undefined && converted !== '') {
        transformedRow[fieldConfig.field_name] = converted;
      } else if (!fieldConfig) {
        // Include fields not in config (like id, is_deleted, item_name, etc.)
        transformedRow[key] = converted;
      }
    });

    // Always preserve id and is_deleted fields
    if (sourceRow.id !== undefined && sourceRow.id !== null && sourceRow.id !== '') {
      transformedRow['id'] = typeof sourceRow.id === 'string' ? sourceRow.id : String(sourceRow.id);
    }

    // Log final transformed row for items table
    const isItemsTable = subTabKey.toLowerCase().includes('item');

    // Preserve is_deleted field from source row
    if (sourceRow.is_deleted !== undefined) {
      const isDeleted = sourceRow.is_deleted === true ||
        sourceRow.is_deleted === 1 ||
        sourceRow.is_deleted === '1' ||
        sourceRow.is_deleted === 'true';
      transformedRow['is_deleted'] = isDeleted;
    }

    // Add extracted name fields (e.g., item_name from item_id object)
    // These fields may not be in the fields array but are expected in the payload
    Object.keys(extractedNames).forEach(nameField => {
      if (extractedNames[nameField]) {
        transformedRow[nameField] = extractedNames[nameField];
      }
    });

    // Add custom fields as nested object if any exist
    if (Object.keys(customFieldsData).length > 0) {
      transformedRow['custom_fields'] = customFieldsData;
    }

    // Final log for items table

    return transformedRow;
  }

  /**
   * Convert value to appropriate data type
   */
  private convertToDataType(value: any, dataType: string, field?: FieldConfig): any {
    if (value === undefined || value === null) {
      return null;
    }

    if (typeof value === 'string' && value.trim() === '') {
      return null;
    }

    const normalizedType = dataType?.toLowerCase();

    // Handle file fields - preserve File objects as-is
    if (field?.field_type === 'file_upload' || normalizedType === 'file') {
      return value;
    }

    if (normalizedType === 'boolean' || this.isBooleanRadioField(field)) {
      return this.normalizeBooleanValue(value);
    }

    // CRITICAL: Check for snowflake ID fields BEFORE any number conversion
    // Snowflake IDs must remain as strings to prevent JavaScript number precision loss
    // even if their data_type is int64 in the schema
    if (field?.field_name && this.isSnowflakeIdField(field.field_name)) {
      return String(value);
    }

    switch (normalizedType) {
      case 'number':
      case 'decimal':
      case 'float':
      case 'double':
      case 'int':
      case 'int64': {
        const numValue = Number(value);
        return Number.isNaN(numValue) ? null : numValue;
      }
      case 'date': {
        // For date type, extract year/month/day from local time and create UTC midnight
        // This preserves the user's intended date regardless of timezone
        if (value instanceof Date) {
          const year = value.getFullYear();
          const month = value.getMonth();
          const day = value.getDate();
          // Create UTC date at midnight with the same year/month/day as local
          const utcDate = new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
          return utcDate.toISOString();
        }
        // Parse the date string and extract components
        const momentDate = moment(value);
        if (!momentDate.isValid()) return null;
        const year = momentDate.year();
        const month = momentDate.month();
        const day = momentDate.date();
        const utcDate = new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
        return utcDate.toISOString();
      }
      case 'datetime':
      case 'timestamp': {
        // For datetime and timestamp, preserve the time
        if (value instanceof Date) {
          return value.toISOString();
        }
        const momentDate = moment(value);
        return momentDate.isValid() ? momentDate.toISOString() : null;
      }
      default:
        // Handle time field by name (even if data_type is not explicitly date/datetime)
        if (field?.field_name === 'time' && (value instanceof Date || typeof value === 'string')) {
          if (value instanceof Date) {
            return value.toISOString();
          }
          const momentDate = moment(value);
          return momentDate.isValid() ? momentDate.toISOString() : null;
        }
        return value;
    }
  }

  /**
   * Check if field is a boolean radio field
   */
  private isBooleanRadioField(field?: FieldConfig): boolean {
    if (!field) return false;
    const normalizedType = field.data_type?.toLowerCase();
    return field.field_type === 'radio' && (normalizedType === 'boolean');
  }

  /**
   * Normalize boolean value
   */
  private normalizeBooleanValue(value: any): boolean | null {
    if (value === undefined || value === null || value === '') {
      return null;
    }

    if (typeof value === 'boolean') {
      return value;
    }

    if (typeof value === 'number') {
      if (value === 1) return true;
      if (value === 2) return false;
      return value !== 0;
    }

    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (['1', 'true', 'yes'].includes(normalized)) return true;
      if (['2', 'false', 'no'].includes(normalized)) return false;
    }

    return Boolean(value);
  }

  /**
   * List of field names that contain snowflake IDs
   * These must be kept as strings to prevent JavaScript number precision loss
   * Snowflake IDs can exceed Number.MAX_SAFE_INTEGER (9007199254740991)
   */
  private static readonly SNOWFLAKE_ID_FIELDS = [
    'fiscal_period_id',
    // Add other snowflake ID fields here if needed in the future
  ];

  /**
   * Check if a field name is a snowflake ID that should be kept as string
   */
  private isSnowflakeIdField(fieldName: string): boolean {
    return PayloadGeneratorService.SNOWFLAKE_ID_FIELDS.includes(fieldName);
  }

  /**
   * Build all field values using fieldToInterfaceMap exclusively
   * All fields are processed uniformly - destination determined solely by fieldToInterfaceMap
   * This allows fields to be moved between tabs on UI without breaking payload generation
   */
  private buildAllFieldValues(
    formValues: any,
    fields: FieldConfig[],
    getFieldKey: (name: string) => string,
    skipFields: string[],
    fieldToInterfaceMap: Record<string, string>,
    extractObjectId: (value: any, fieldName: string) => any,
    entityId: string | number | undefined,
    dynamicFormTabs: any[],
    primaryKey: string | null
  ): Record<string, Record<string, any>> {
    const fieldData: Record<string, Record<string, any>> = {};

    // Process all fields uniformly - destination determined by fieldToInterfaceMap
    fields.forEach(field => {
      // Skip table fields (they're handled separately in buildTableFieldValues)
      if (this.isFieldInTable(field, dynamicFormTabs)) {
        return;
      }

      // Skip specified fields
      if (skipFields.includes(field.field_name)) {
        return;
      }

      const key = getFieldKey(field.field_name);
      let value = formValues[key];
      // Extract ID from object if needed (for all fields, not just those ending with _id)
      // This handles select/autocomplete fields that return objects
      if (value && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
        value = extractObjectId(value, field.field_name);
      }

      // Check if extracted value is an empty object
      if (value && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
        if (Object.keys(value).length === 0) {
          // Skip empty objects
          return;
        }
      }

      // After extraction, check if value is meaningful
      if (value === undefined || value === null || value === '') {
        return;
      }

      // Determine destination using fieldToInterfaceMap exclusively
      // If fieldToInterfaceMap is provided, use it; otherwise, use primary entity as fallback
      let destination: string;

      if (fieldToInterfaceMap && Object.keys(fieldToInterfaceMap).length > 0) {
        // Use fieldToInterfaceMap as the source of truth
        if (fieldToInterfaceMap[field.field_name]) {
          destination = fieldToInterfaceMap[field.field_name];
        } else {
          // If field is not in map, use primary entity as default
          destination = primaryKey || 'primary';
        }
      } else {
        // If fieldToInterfaceMap is not provided, use primary entity as fallback
        destination = primaryKey || 'primary';
      }

      // Initialize destination object if it doesn't exist
      if (!fieldData[destination]) {
        fieldData[destination] = {};
      }

      // Ensure snowflake ID fields are kept as strings to prevent JavaScript number precision loss
      // These IDs can exceed Number.MAX_SAFE_INTEGER (9007199254740991)
      if (this.isSnowflakeIdField(field.field_name) && value !== null && value !== undefined) {
        value = String(value);
      }

      // Add field to destination
      fieldData[destination][field.field_name] = value;
    });

    // Add entity ID to primary entity if provided
    if (entityId !== undefined) {
      // Determine the primary entity key
      let targetKey: string;

      if (primaryKey) {
        // Use provided primary key
        targetKey = primaryKey;
      } else {
        // Fallback: use first key from fieldData if available, or first value from fieldToInterfaceMap
        const fieldDataKeys = Object.keys(fieldData);
        if (fieldDataKeys.length > 0) {
          targetKey = fieldDataKeys[0];
        } else {
          // If no field data exists yet, use first value from fieldToInterfaceMap or default
          const interfaceMapValues = Object.values(fieldToInterfaceMap);
          if (interfaceMapValues.length > 0) {
            // Find the primary entity key (usually the first unique value that appears most frequently)
            // For most cases, it's the first value in the map
            targetKey = interfaceMapValues[0];
          } else {
            // Last resort: use a generic key (shouldn't happen in practice)
            targetKey = 'primary';
          }
        }
      }

      // Initialize target object if it doesn't exist
      if (!fieldData[targetKey]) {
        fieldData[targetKey] = {};
      }

      // Add entity ID
      fieldData[targetKey]['id'] = entityId;
    }

    return fieldData;
  }

  /**
   * Build table/array field values
   */
  private buildTableFieldValues(
    formValues: any,
    dynamicFormTabs: any[],
    tableFormArrays: Map<string, FormArray>,
    getFieldKey: (name: string) => string,
    tableToInterfaceMap: Record<string, string>,
    extractObjectId: (value: any, fieldName: string) => any
  ): Record<string, any[]> {
    const tableData: Record<string, any[]> = {};

    dynamicFormTabs.forEach(tab => {
      tab.sub_tabs?.forEach((subTab: any) => {
        if (!this.isTableSubTab(subTab)) {
          return;
        }

        const tableKey = getFieldKey(subTab.key || subTab.name || '');
        const tableArray = tableFormArrays.get(tableKey);

        let rows: any[] = [];

        if (tableArray && tableArray.length > 0) {
          rows = tableArray.controls
            .map((control: AbstractControl) => {
              if (!(control instanceof FormGroup)) {
                return null;
              }
              const rowValue = control.getRawValue();
              const transformedRow = this.transformTableRow(rowValue, subTab, getFieldKey, extractObjectId);

              const isDeletedControl = control.get('is_deleted');
              if (isDeletedControl) {
                const isDeletedValue = isDeletedControl.value;
                if (isDeletedValue !== undefined) {
                  transformedRow['is_deleted'] = isDeletedValue;
                }
              }

              return this.normalizeTableRow(transformedRow);
            })
            .filter((row: any) => row !== null && this.hasMeaningfulRowData(row));
        }

        if (rows.length > 0) {
          // Use subTab.key (system_name from backend) as primary source, but allow tableToInterfaceMap to override
          let tableName: string | null = null;

          // First try: use tableToInterfaceMap if provided (for explicit mappings like items_table -> items)
          if (tableToInterfaceMap && Object.keys(tableToInterfaceMap).length > 0) {
            const subTabKey = subTab.key || subTab.name || '';
            const normalizedKey = subTabKey.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/(^_|_$)/g, '');

            if (tableToInterfaceMap[subTabKey]) {
              tableName = tableToInterfaceMap[subTabKey];
            } else if (tableToInterfaceMap[normalizedKey]) {
              tableName = tableToInterfaceMap[normalizedKey];
            } else {
              const tabKey = `${tab.id}_${subTab.id}`;
              if (tableToInterfaceMap[tabKey]) {
                tableName = tableToInterfaceMap[tabKey];
              }
            }
          }

          // Second try: use subTab.key (system_name from backend API) - this matches interface structure
          if (!tableName && subTab.key) {
            tableName = subTab.key;
          }

          // Last fallback: use slugified name (should not happen if backend provides system_name)
          if (!tableName) {
            const name = subTab.key || subTab.name || '';
            tableName = name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/(^_|_$)/g, '');
          }

          if (tableName) {
            tableData[tableName] = rows;
          }
        }
      });
    });

    return tableData;
  }


  /**
   * Normalize a single table row (ensure is_deleted is included)
   */
  private normalizeTableRow(row: any): any {
    const normalizedRow: any = { ...row };

    // Ensure is_deleted is included and properly set
    const isDeleted = normalizedRow.is_deleted === true ||
      normalizedRow.is_deleted === 1 ||
      normalizedRow.is_deleted === '1' ||
      normalizedRow.is_deleted === 'true';

    // Always include is_deleted field in the payload
    normalizedRow.is_deleted = isDeleted ? true : false;

    return normalizedRow;
  }

  /**
   * Check if a row has meaningful data (not empty)
   * Returns true if row should be included in submission
   */
  private hasMeaningfulRowData(row: Record<string, any>): boolean {
    // Always include rows with is_deleted = true (soft deleted rows)
    const isDeleted = row['is_deleted'] === true ||
      row['is_deleted'] === 1 ||
      row['is_deleted'] === '1' ||
      row['is_deleted'] === 'true';
    if (isDeleted) {
      return true;
    }

    // Always include rows with an id (existing rows that might be updated)
    if (row['id'] !== undefined && row['id'] !== null && String(row['id']).trim() !== '') {
      return true;
    }

    // Check if row has any meaningful field values
    return Object.entries(row).some(([key, value]) => {
      // Skip internal fields
      if (key === 'is_deleted' || key === 'id' || key === '_selected') {
        return false;
      }

      if (value === undefined || value === null) {
        return false;
      }

      if (Array.isArray(value)) {
        return value.length > 0;
      }

      if (typeof value === 'string') {
        return value.trim() !== '';
      }

      if (typeof value === 'number') {
        return value !== 0;
      }

      if (typeof value === 'boolean') {
        return true; // Boolean values are meaningful
      }

      if (value instanceof Date) {
        return true; // Dates are meaningful
      }

      if (typeof value === 'object') {
        // Check if object has any properties
        return Object.keys(value).length > 0;
      }

      return true;
    });
  }

  /**
   * Collect custom fields
   */
  private collectCustomFields(
    formValues: any,
    fields: FieldConfig[],
    getFieldKey: (name: string) => string
  ): Record<string, any> {
    const customFields: Record<string, any> = {};

    const customFieldConfigs = fields.filter(field => field.is_custom_field);

    customFieldConfigs.forEach(field => {
      const key = getFieldKey(field.field_name);
      const value = formValues[key];

      if (value !== undefined && value !== null && value !== '') {
        customFields[field.field_name] = value;
      }
    });

    return customFields;
  }

  /**
   * Merge tabs (e.g., bank_information into account_information)
   * Uses keys from tabData directly (which are already mapped via fieldToInterfaceMap)
   */
  private mergeTabs(
    tabData: Record<string, Record<string, any>>,
    mergeTabs: Record<string, string>
  ): void {
    Object.entries(mergeTabs).forEach(([sourceTab, targetTab]) => {
      // Find source and target tab names
      let sourceTabName: string | null = null;
      let targetTabName: string | null = null;

      // First, check if sourceTab matches any key in tabData directly
      const tabDataKeys = Object.keys(tabData);
      const normalizedSourceTab = sourceTab.toLowerCase().replace(/[^a-z0-9]/g, '_');
      const normalizedTargetTab = targetTab.toLowerCase().replace(/[^a-z0-9]/g, '_');

      // Check tabData keys directly
      for (const tabKey of tabDataKeys) {
        const normalizedTabKey = tabKey.toLowerCase().replace(/[^a-z0-9]/g, '_');
        if (normalizedTabKey === normalizedSourceTab || normalizedTabKey.includes(normalizedSourceTab)) {
          sourceTabName = tabKey;
        }
        if (normalizedTabKey === normalizedTargetTab || normalizedTabKey.includes(normalizedTargetTab)) {
          targetTabName = tabKey;
        }
      }


      // If still not found, use the targetTab as-is (it might be the final interface key)
      if (!targetTabName) {
        targetTabName = targetTab;
      }

      if (!sourceTabName || !tabData[sourceTabName] || Object.keys(tabData[sourceTabName]).length === 0) {
        return;
      }

      // Ensure target tab exists
      const finalTargetTabName: string = targetTabName || targetTab;
      if (!finalTargetTabName) {
        return; // Cannot proceed without target tab name
      }

      if (!tabData[finalTargetTabName]) {
        tabData[finalTargetTabName] = {};
      }

      // Merge all source fields into target
      const sourceTabData = tabData[sourceTabName];
      if (sourceTabData) {
        Object.keys(sourceTabData).forEach(fieldName => {
          const value = sourceTabData[fieldName];
          if (value !== undefined && value !== null && value !== '') {
            tabData[finalTargetTabName][fieldName] = value;
          }
        });
      }

      // Remove source tab
      if (sourceTabName) {
        delete tabData[sourceTabName];
      }
    });
  }

  /**
   * Include struct fields not in template array
   */
  private includeStructFields(
    payload: any,
    interfaceKeys: string[],
    formValues: any,
    getFieldKey: (name: string) => string,
    extractObjectId: (value: any, fieldName: string) => any
  ): void {
    interfaceKeys.forEach(key => {
      // Skip if already in payload
      if (payload.hasOwnProperty(key)) {
        return;
      }

      // Try to find in form values
      const fieldKey = getFieldKey(key);
      if (formValues.hasOwnProperty(fieldKey)) {
        let value = formValues[fieldKey];

        // Skip if value is empty
        if (value === undefined || value === null || value === '') {
          return;
        }

        // Extract ID from object if needed
        if (key.endsWith('_id') || key.endsWith('_name')) {
          value = extractObjectId(value, key);
        }

        // Check if extracted value is an empty object
        if (value && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
          if (Object.keys(value).length === 0) {
            // Skip empty objects
            return;
          }
        }

        // Only add if value is meaningful
        if (value !== undefined && value !== null && value !== '') {
          payload[key] = value;
        }
      }
    });
  }


  /**
   * Check if sub-tab is a table
   */
  private isTableSubTab(subTab: any): boolean {
    return subTab.sub_type === 'table' ||
      subTab.sub_type === 'TABLE' ||
      subTab.type === 'table' ||
      subTab.type === 'TABLE';
  }

  /**
   * Check if a field belongs to a table sub-tab by looking it up in dynamicFormTabs
   */
  private isFieldInTable(field: FieldConfig, dynamicFormTabs: any[]): boolean {
    if (!dynamicFormTabs || dynamicFormTabs.length === 0) {
      // Fallback: check field properties directly if dynamicFormTabs not available
      return field.sub_type === 'table' ||
        field.sub_type === 'TABLE' ||
        field.type === 'table' ||
        field.type === 'TABLE';
    }

    // First check if field has sub_tab_key that indicates it's in a table
    // Common table sub_tab_keys: 'items', 'expenses', 'items_table', 'expenses_table', etc.
    if (field.sub_tab_key) {
      const subTabKey = field.sub_tab_key.toLowerCase();
      if (subTabKey.includes('item') || subTabKey.includes('expense') ||
        subTabKey.includes('table') || subTabKey.includes('service')) {
        // Verify it's actually a table by checking dynamicFormTabs
        for (const tab of dynamicFormTabs) {
          if (tab.sub_tabs && Array.isArray(tab.sub_tabs)) {
            for (const subTab of tab.sub_tabs) {
              const subTabKeyMatch = subTab.key?.toLowerCase() ||
                subTab.name?.toLowerCase() ||
                '';
              if (subTabKeyMatch === subTabKey ||
                subTabKeyMatch.includes(subTabKey) ||
                subTabKey.includes(subTabKeyMatch)) {
                if (this.isTableSubTab(subTab)) {
                  return true;
                }
              }
            }
          }
        }
      }
    }

    // Check if the field's tab_id and sub_tab_id correspond to a table subTab
    for (const tab of dynamicFormTabs) {
      // Match tab by both id and value (tab.value might be the actual tab ID in some cases)
      const tabMatches = Number(tab.id) === Number(field.tab_id) ||
        Number(tab.value) === Number(field.tab_id);

      if (tabMatches && tab.sub_tabs && Array.isArray(tab.sub_tabs)) {
        for (const subTab of tab.sub_tabs) {
          // Check if subTab.id matches AND it's actually a table subTab
          if (Number(subTab.id) === Number(field.sub_tab_id)) {
            // If it's a table subTab, return true
            if (this.isTableSubTab(subTab)) {
              return true;
            }
            // If it's not a table subTab, we found the matching subTab but it's not a table
            // So we can break early (no need to check other subTabs in this tab)
            break;
          }
        }
      }
    }

    // Final fallback: check field properties directly
    return field.sub_type === 'table' ||
      field.sub_type === 'TABLE' ||
      field.type === 'table' ||
      field.type === 'TABLE';
  }

  /**
   * Get default tab name
   */
  private getDefaultTabName(tabId: string | number, subTabId: number): string {
    return `${tabId}_${subTabId}`;
  }

  /**
   * Default function to extract ID from autocomplete/select object
   */
  private defaultExtractObjectId(value: any, fieldName: string): any {
    if (value === null || value === undefined || value === '') {
      return value;
    }

    // If value is an object (from autocomplete/select), extract ID
    if (typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
      const extracted = value.id ?? value.value ?? value[fieldName] ?? null;
      // If extracted value is still an object (empty object), return null
      if (extracted && typeof extracted === 'object' && !Array.isArray(extracted) && !(extracted instanceof Date)) {
        if (Object.keys(extracted).length === 0) {
          return null;
        }
      }
      return extracted;
    }

    return value;
  }

  /**
   * Check if a tab/subtab is visible in dynamicFormTabs
   */
  private isTabSubTabVisible(
    field: FieldConfig,
    dynamicFormTabs: any[]
  ): boolean {
    if (!dynamicFormTabs || dynamicFormTabs.length === 0) {
      // If dynamicFormTabs is not available, assume visible (fallback to original behavior)
      return true;
    }

    if (field.tab_id === 1 && field.sub_tab_id <= 1) {
      return true;
    }

    // Find the tab in dynamicFormTabs
    const tab = dynamicFormTabs.find((t: any) =>
      Number(t.value) === Number(field.tab_id) ||
      Number(t.id) === Number(field.tab_id)
    );

    if (!tab) {
      // Tab not found in dynamicFormTabs - it's not visible
      return false;
    }

    // Check if tab has is_active/is_deleted properties
    if (tab.hasOwnProperty('is_active') && !tab.is_active) {
      return false;
    }
    if (tab.hasOwnProperty('is_deleted') && tab.is_deleted) {
      return false;
    }

    // Find the subtab in the tab's sub_tabs
    if (!tab.sub_tabs || !Array.isArray(tab.sub_tabs) || tab.sub_tabs.length === 0) {
      // If no sub_tabs array, check if field is in primary tab
      // Primary tab fields might not have sub_tabs structure
      return Number(field.tab_id) === Number(tab.value) || Number(field.tab_id) === Number(tab.id);
    }

    const subTab = tab.sub_tabs.find((st: any) =>
      Number(st.id) === Number(field.sub_tab_id)
    );

    if (!subTab) {
      // Subtab not found - it's not visible
      return false;
    }

    // Check if subtab has is_active/is_deleted properties
    if (subTab.hasOwnProperty('is_active') && !subTab.is_active) {
      return false;
    }
    if (subTab.hasOwnProperty('is_deleted') && subTab.is_deleted) {
      return false;
    }

    return true;
  }

  /**
   * Validate required fields
   */
  private validateRequiredFields(
    fields: FieldConfig[],
    payload: any,
    getFieldKey: (name: string) => string,
    skipFields: string[],
    fieldToInterfaceMap: Record<string, string> = {},
    formGroup?: FormGroup,
    dynamicFormTabs: any[] = []
  ): void {
    // Track missing fields with their table and row information
    // Map structure: tableName -> Map<rowIndex, fieldNames[]>
    const missingFieldsByTableAndRow: Map<string, Map<number, string[]>> = new Map();
    const missingFieldsRegular: string[] = [];

    fields.forEach(field => {


      // Check if field is in a table
      const tableInfo = this.getTableInfoForField(field, dynamicFormTabs, getFieldKey);
      const isTableField = tableInfo !== null;

      // Handle table fields separately - validate them per row
      if (isTableField && formGroup && tableInfo) {
        const tableKey = tableInfo.tableKey;
        const tableArray = formGroup.get(tableKey) as FormArray;

        if (tableArray && tableArray.length > 0) {
          // Validate each row in the table
          tableArray.controls.forEach((rowControl: AbstractControl, rowIndex: number) => {
            if (rowControl instanceof FormGroup) {
              const fieldKey = getFieldKey(field.field_name);
              const control = rowControl.get(fieldKey);

              if (field.is_mandatory && !skipFields.includes(field.field_name)) {
                let hasValue = false;

                if (control) {
                  const rawValue = (control as any).getRawValue ? (control as any).getRawValue() : control.value;
                  const value = rawValue !== undefined ? rawValue : control.value;

                  if (value !== undefined && value !== null && value !== '') {
                    if (typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
                      hasValue = Object.keys(value).length > 0;
                    } else {
                      hasValue = true;
                    }
                  }
                }

                if (!hasValue) {
                  const fieldDisplayName = this.normalizeFieldName(field.display_name || field.field_name);

                  // Initialize table map if it doesn't exist
                  if (!missingFieldsByTableAndRow.has(tableInfo.tableName)) {
                    missingFieldsByTableAndRow.set(tableInfo.tableName, new Map());
                  }

                  const tableMap = missingFieldsByTableAndRow.get(tableInfo.tableName)!;

                  // Initialize row map if it doesn't exist
                  if (!tableMap.has(rowIndex)) {
                    tableMap.set(rowIndex, []);
                  }

                  // Add field to the row (avoid duplicates)
                  const rowFields = tableMap.get(rowIndex)!;
                  if (!rowFields.includes(fieldDisplayName)) {
                    rowFields.push(fieldDisplayName);
                  }
                }
              }
            }
          });
        }
        return; // Skip regular validation for table fields
      }

      // Skip validation for fields that are not visible
      if (field.hasOwnProperty('is_visible') && field.is_visible === false) {
        return;
      }

      // Skip validation for fields in tabs/subtabs that are not visible or not present
      if (!this.isTabSubTabVisible(field, dynamicFormTabs)) {
        return;
      }

      if (field.is_mandatory && !skipFields.includes(field.field_name)) {
        const fieldKey = getFieldKey(field.field_name);

        // For custom fields, check in custom_fields object
        if (field.is_custom_field) {
          let hasValue = false;

          // First, check if field has a value in formGroup
          if (formGroup) {
            const control = formGroup.get(fieldKey);
            if (control) {
              const rawValue = (control as any).getRawValue ? (control as any).getRawValue() : control.value;
              const value = rawValue !== undefined ? rawValue : control.value;

              if (value !== undefined && value !== null && value !== '') {
                if (typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
                  hasValue = Object.keys(value).length > 0;
                } else {
                  hasValue = true;
                }
              }
            } else {
              // Check raw form values
              const rawFormValues = formGroup.getRawValue?.() ?? formGroup.value;
              if (rawFormValues && typeof rawFormValues === 'object') {
                const possibleKeys = [fieldKey, field.field_name];
                for (const key of possibleKeys) {
                  if (rawFormValues.hasOwnProperty(key)) {
                    const value = rawFormValues[key];
                    if (value !== undefined && value !== null && value !== '') {
                      hasValue = true;
                      break;
                    }
                  }
                }
              }
            }
          }

          // Check in payload custom_fields object
          if (!hasValue && payload.custom_fields && typeof payload.custom_fields === 'object') {
            hasValue = this.hasFieldValueInObject(payload.custom_fields, field.field_name);
          }

          if (!hasValue) {
            const fieldDisplayName = this.normalizeFieldName(field.display_name || field.field_name);
            missingFieldsRegular.push(fieldDisplayName);
          }
        } else {
          // Regular field validation
          // First, check if field has a value in formGroup (most reliable check)
          let hasValue = false;
          if (formGroup) {
            // Try to get control directly
            const control = formGroup.get(fieldKey);
            if (control) {
              // Use getRawValue() to include disabled fields, fallback to value
              const rawValue = (control as any).getRawValue ? (control as any).getRawValue() : control.value;
              const value = rawValue !== undefined ? rawValue : control.value;

              // Check if value is meaningful (not empty string, null, undefined, or empty object)
              if (value !== undefined && value !== null && value !== '') {
                // If it's an object, check if it has meaningful properties
                if (typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
                  hasValue = Object.keys(value).length > 0;
                } else {
                  hasValue = true;
                }
              }
            } else {
              // Control not found via get(), check raw form values directly
              // Try multiple key variations to find the value
              const rawFormValues = formGroup.getRawValue?.() ?? formGroup.value;
              if (rawFormValues && typeof rawFormValues === 'object') {
                // Try fieldKey, fieldName, and original field.field_name
                const possibleKeys = [fieldKey, field.field_name];
                // Also try lowercase variations
                possibleKeys.push(fieldKey.toLowerCase(), field.field_name.toLowerCase());

                let value: any = undefined;
                for (const key of possibleKeys) {
                  if (rawFormValues.hasOwnProperty(key)) {
                    value = rawFormValues[key];
                    break;
                  }
                }

                if (value !== undefined && value !== null && value !== '') {
                  // If it's an object, check if it has meaningful properties
                  if (typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
                    hasValue = Object.keys(value).length > 0;
                  } else {
                    hasValue = true;
                  }
                }
              }
            }
          }

          // If not found in formGroup, check in payload structure
          // Use fieldToInterfaceMap exclusively to determine where to check
          if (!hasValue) {
            let checkLocation: string | null = null;

            // Use fieldToInterfaceMap exclusively
            if (fieldToInterfaceMap && Object.keys(fieldToInterfaceMap).length > 0) {
              if (fieldToInterfaceMap[field.field_name]) {
                checkLocation = fieldToInterfaceMap[field.field_name];
              } else {
                // If field is not in map, check in primary entity (first key in payload)
                const payloadKeys = Object.keys(payload);
                checkLocation = payloadKeys.length > 0 ? payloadKeys[0] : null;
              }
            } else {
              // If fieldToInterfaceMap is not provided, check in primary entity (first key in payload)
              const payloadKeys = Object.keys(payload);
              checkLocation = payloadKeys.length > 0 ? payloadKeys[0] : null;
            }

            // Check in the determined location
            if (checkLocation && payload[checkLocation] && typeof payload[checkLocation] === 'object') {
              hasValue = this.hasFieldValueInObject(payload[checkLocation], fieldKey);
            }
          }

          // Final fallback: check in payload root level (check both fieldKey and fieldName)
          if (!hasValue) {
            hasValue = this.hasFieldValue(payload, fieldKey);
          }

          if (!hasValue) {
            const fieldDisplayName = this.normalizeFieldName(field.display_name || field.field_name);
            missingFieldsRegular.push(fieldDisplayName);
          }
        }
      }
    });

    // Build formatted error message
    const errorMessages: string[] = [];

    // Group regular missing fields
    if (missingFieldsRegular.length > 0) {
      const uniqueRegularFields = Array.from(new Set(missingFieldsRegular));
      errorMessages.push(`Missing required fields: ${uniqueRegularFields.join(', ')}`);
    }

    // Group table missing fields by table and row
    if (missingFieldsByTableAndRow.size > 0) {
      missingFieldsByTableAndRow.forEach((rowMap, tableName) => {
        const normalizedTableName = this.normalizeTableName(tableName);

        // Sort rows by index for consistent ordering
        const sortedRows = Array.from(rowMap.entries()).sort((a, b) => a[0] - b[0]);

        sortedRows.forEach(([rowIndex, fields]) => {
          const uniqueFields = Array.from(new Set(fields));
          // Row index is 0-based, but display as 1-based for user-friendly message
          const displayRowNumber = rowIndex + 1;
          errorMessages.push(`Missing required fields in ${normalizedTableName} table row ${displayRowNumber}: ${uniqueFields.join(', ')}`);
        });
      });
    }

    if (errorMessages.length > 0) {
      throw new PayloadValidationError(errorMessages);
    }
  }

  /**
   * Get table information for a field (if it belongs to a table)
   */
  private getTableInfoForField(
    field: FieldConfig,
    dynamicFormTabs: any[],
    getFieldKey: (name: string) => string
  ): { tableKey: string; tableName: string } | null {
    if (!dynamicFormTabs || dynamicFormTabs.length === 0) {
      return null;
    }

    for (const tab of dynamicFormTabs) {
      if (tab.sub_tabs && Array.isArray(tab.sub_tabs)) {
        for (const subTab of tab.sub_tabs) {
          if (this.isTableSubTab(subTab)) {
            // Check if field belongs to this table
            if (Number(subTab.id) === Number(field.sub_tab_id) &&
              (Number(tab.id) === Number(field.tab_id) || Number(tab.value) === Number(field.tab_id))) {
              const tableKey = getFieldKey(subTab.key || subTab.name || '');
              const tableName = subTab.key || subTab.name || subTab.title || 'Unknown Table';
              return { tableKey, tableName };
            }
          }
        }
      }
    }

    return null;
  }

  /**
   * Normalize field name for better UI display
   */
  private normalizeFieldName(fieldName: string): string {
    if (!fieldName) return fieldName;

    // If it's already in a readable format (has spaces or capitals), return as is
    if (/[A-Z]/.test(fieldName) || fieldName.includes(' ')) {
      return fieldName;
    }

    // Convert snake_case to Title Case
    return fieldName
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  /**
   * Normalize table name for better UI display
   */
  private normalizeTableName(tableName: string): string {
    if (!tableName) return tableName;

    // If it's already in a readable format, return as is
    if (/[A-Z]/.test(tableName) || tableName.includes(' ')) {
      return tableName;
    }

    // Convert snake_case or camelCase to Title Case
    return tableName
      .replace(/([A-Z])/g, ' $1') // Add space before capital letters
      .replace(/_/g, ' ') // Replace underscores with spaces
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
      .trim();
  }

  /**
   * Check if field has a value in a specific object
   */
  private hasFieldValueInObject(obj: any, fieldName: string): boolean {
    if (!obj || typeof obj !== 'object') {
      return false;
    }

    // Check direct property
    if (obj.hasOwnProperty(fieldName)) {
      const value = obj[fieldName];
      // Handle empty objects, empty arrays, and empty strings
      if (value === undefined || value === null || value === '') {
        return false;
      }
      // If it's an object (from autocomplete/select), check if it has meaningful data
      if (typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
        // Object is considered valid if it has at least one property
        return Object.keys(value).length > 0;
      }
      return true;
    }

    return false;
  }

  /**
   * Check if field has a value in payload (recursive check for nested objects)
   */
  private hasFieldValue(payload: any, fieldKey: string): boolean {
    // Direct check
    if (payload.hasOwnProperty(fieldKey)) {
      const value = payload[fieldKey];
      return value !== undefined && value !== null && value !== '';
    }

    // Check nested objects (e.g., vendor.company_name)
    const parts = fieldKey.split('.');
    if (parts.length > 1) {
      let current = payload;
      for (const part of parts) {
        if (current && typeof current === 'object' && current.hasOwnProperty(part)) {
          current = current[part];
        } else {
          return false;
        }
      }
      return current !== undefined && current !== null && current !== '';
    }

    // Check in nested objects (e.g., payload.vendor.field_name)
    for (const key in payload) {
      if (payload.hasOwnProperty(key)) {
        const nestedObj = payload[key];
        if (nestedObj && typeof nestedObj === 'object' && nestedObj !== null && !Array.isArray(nestedObj) && !(nestedObj instanceof Date)) {
          if (nestedObj.hasOwnProperty(fieldKey)) {
            const value = nestedObj[fieldKey];
            return value !== undefined && value !== null && value !== '';
          }
        }
      }
    }

    return false;
  }

  /**
   * Default field key transformation
   */
  private defaultGetFieldKey(fieldName: string): string {
    return fieldName.toLowerCase().replace(/[^a-z0-9]/g, '_');
  }

  /**
   * Clean up empty objects in payload - convert to null or remove them
   * Only cleans base level and one level deep (no recursion, no stacks, no queues)
   */
  private cleanupEmptyObjects(payload: any): void {
    if (!payload || typeof payload !== 'object' || Array.isArray(payload) || payload instanceof Date) {
      return;
    }

    // Base level cleanup
    const keys = Object.keys(payload);
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      const value = payload[key];

      // Remove undefined values
      if (value === undefined) {
        delete payload[key];
        continue;
      }

      // Handle arrays - clean array items (one level deep)
      if (Array.isArray(value)) {
        for (let j = 0; j < value.length; j++) {
          const item = value[j];
          if (item && typeof item === 'object' && !Array.isArray(item) && !(item instanceof Date)) {
            const itemKeys = Object.keys(item);
            if (itemKeys.length === 0) {
              value[j] = null;
            } else {
              // Clean one level deep in array items
              for (let k = 0; k < itemKeys.length; k++) {
                const itemKey = itemKeys[k];
                const itemValue = item[itemKey];
                if (itemValue === undefined) {
                  delete item[itemKey];
                } else if (itemValue && typeof itemValue === 'object' && !Array.isArray(itemValue) && !(itemValue instanceof Date)) {
                  if (Object.keys(itemValue).length === 0) {
                    item[itemKey] = null;
                  }
                }
              }
              // Check if item became empty after cleanup
              if (Object.keys(item).length === 0) {
                value[j] = null;
              }
            }
          }
        }
        continue;
      }

      // Handle objects - clean one level deep
      if (value && typeof value === 'object' && !(value instanceof Date)) {
        const valueKeys = Object.keys(value);
        if (valueKeys.length === 0) {
          // Empty object - convert to null
          payload[key] = null;
        } else {
          // Clean one level deep
          for (let j = 0; j < valueKeys.length; j++) {
            const nestedKey = valueKeys[j];
            const nestedValue = value[nestedKey];

            if (nestedValue === undefined) {
              delete value[nestedKey];
            } else if (nestedValue && typeof nestedValue === 'object' && !Array.isArray(nestedValue) && !(nestedValue instanceof Date)) {
              if (Object.keys(nestedValue).length === 0) {
                value[nestedKey] = null;
              }
            }
          }
          // Check if object became empty after cleanup
          if (Object.keys(value).length === 0) {
            payload[key] = null;
          }
        }
      }
    }
  }
}
